import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-helpers';
import { useDataApi } from '@adsk/alloy-helpers';
import { useCurrentAccountId } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import moment from 'moment';
import {
  useCreateAccountCompany,
  useGetAccountCompanies,
  useAnalytics,
} from '@common/hooks';
import { OP_STATUSES, SERVICE_ACCESSES, TRACK_EVENTS } from '@common/constants';
import { ACCOUNT_LEVEL_ACCESSES } from './constants';
import {
  useAssignOffering,
  useGetAccountRoles,
  useOfferingsEvents,
  useUnassignOffering,
  useUserOfferings,
  useUserSheet,
  useUserSubscriptions,
} from './hooks';
import { ACTIONS } from './state/actions';
import { reducer, initialState } from './state/reducer';
import { accountUserPropType } from './propTypes';
import {
  buildGetAccountCompanyRequest,
  buildGetAccountRoleRequest,
  buildUpdateAccountMemberRequest,
} from './utils';
import AccountMemberProfilePanel from './component';

const AccountMemberProfilePanelContainer = ({
  loadingOfferings,
  offerings,
  offeringsDataError,
  onSaveData,
  onTryAgainClick,
  onUpdateOffering,
  user,
  ...rest
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const [state, dispatch] = useReducer(reducer, initialState);
  const { trackEvent } = useAnalytics();
  const accountId = useCurrentAccountId();
  const latestOnSave = useRef(onSaveData);
  const licenseDropdownPreviousValue = useRef(null);
  const licenseLicenseChangeTimeStamp = useRef(null);

  const [accessLevels, setAccessLevels] = useState([]);
  const ACCESSES = useMemo(
    () => ({
      accountAdmin: {
        label: T.translate('ACCOUNT_ADMIN'),
        value: ACCOUNT_LEVEL_ACCESSES.ACCOUNT_ADMIN,
      },
      executive: {
        label: T.translate('EXECUTIVE'),
        value: ACCOUNT_LEVEL_ACCESSES.EXECUTIVE,
      },
      projectAdmin: {
        label: T.translate('PROJECT_ADMIN'),
        value: SERVICE_ACCESSES.ADMIN,
      },
      projectMember: {
        label: T.translate('PROJECT_MEMBER'),
        value: SERVICE_ACCESSES.MEMBER,
      },
    }),
    [T]
  );

  const [isUpdatingAccessLevel, setIsUpdatingAccessLevel] = useState(false);
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const { showNotification } = useNotifications();

  useEffect(() => {
    if (onSaveData) latestOnSave.current = onSaveData;
  }, [onSaveData]);

  const {
    doFetchUserOfferings,
    isFetchingUserOfferings,
    userOfferingsData,
    userOfferingsDataError,
    setUserOfferings,
  } = useUserOfferings({ accountId, dispatch, user });

  const sendAnalytics = useCallback(
    (type, status, categoryName, selectedOffering) => {
      const previouseSelectedCount =
        licenseDropdownPreviousValue.current &&
        licenseDropdownPreviousValue.current.quantity;
      const subscription =
        type === TRACK_EVENTS.NAME.SUBSCRIPTION_UNASSIGN
          ? licenseDropdownPreviousValue.current
          : selectedOffering;

      trackEvent(`${TRACK_EVENTS.SCOPE.LICENSE}.${type}`, {
        actionTimeStamp: moment(licenseLicenseChangeTimeStamp.current).format(),
        actionTimeTaken:
          (Date.now() - licenseLicenseChangeTimeStamp.current) / 1000,
        actionOutcome: status,
        offeringCategory: categoryName,
        subscription: subscription ? subscription.name : '',
        countAvailableNewSelection:
          selectedOffering && selectedOffering.quantity
            ? selectedOffering.quantity.available
            : '',
        countPurchasedNewSelection:
          selectedOffering && selectedOffering.quantity
            ? selectedOffering.quantity.assigned
            : '',
        countAvailableOldSelection: previouseSelectedCount
          ? previouseSelectedCount.available
          : '',
        countPurchasedOldSelection: previouseSelectedCount
          ? previouseSelectedCount.assigned
          : '',
        currentUserEmailDomain: user?.email.substring(
          user?.email.lastIndexOf('@') + 1
        ),
      });
    },
    [trackEvent, user]
  );

  const { assignDropdownLicense, loadingAssignOfferingData } =
    useAssignOffering({
      accountId,
      dispatch,
      licenseDropdownPreviousValue,
      onUpdateOffering,
      user,
      userOfferingsData,
      sendAnalytics,
      setUserOfferings,
      state,
    });

  const { unassignDropdownLicense, loadingUnassignOfferingData } =
    useUnassignOffering({
      accountId,
      assignDropdownLicense,
      dispatch,
      licenseDropdownPreviousValue,
      onUpdateOffering,
      user,
      userOfferingsData,
      sendAnalytics,
      setUserOfferings,
      state,
    });

  const { handleLicenseChange, handleTryAgainClick, savingInputInfo } =
    useOfferingsEvents({
      accountId,
      assignDropdownLicense,
      dispatch,
      doFetchUserOfferings,
      licenseDropdownPreviousValue,
      licenseLicenseChangeTimeStamp,
      loadingAssignOfferingData,
      loadingUnassignOfferingData,
      offerings,
      offeringsDataError,
      onTryAgainClick,
      unassignDropdownLicense,
      user,
      userOfferingsData,
      userOfferingsDataError,
      state,
    });

  const overSheetLimitData = useUserSheet({
    user,
    userOfferingsData,
  });

  const { licensingTrialMessages } = useUserSubscriptions({
    accountId,
    classifiedOfferings: state?.subscription?.currentOfferings,
    saved: savingInputInfo.saved,
    userId: user?.id,
    userName: user?.firstName,
  });

  const { accountCompanies, onChangeCompanyInput, isFetchingAccountCompanies } =
    useGetAccountCompanies();

  const latestOnChangeCompanyInput = useRef(onChangeCompanyInput);

  useEffect(() => {
    latestOnChangeCompanyInput.current = onChangeCompanyInput;
  }, [onChangeCompanyInput]);

  useEffect(() => {
    const handler = setTimeout(() => {
      latestOnChangeCompanyInput.current(
        state.member.companySearchInput.trim()
      );
    }, 800);

    return () => clearTimeout(handler);
  }, [state.member.companySearchInput]);

  const { data: accountCompanyData, doFetch: doFetchAccountCompany } =
    useDataApi();

  // Get the member's initial company name
  useEffect(() => {
    if (!accountId || !user?.defaultCompanyId) return;

    doFetchAccountCompany(
      buildGetAccountCompanyRequest(accountId, user?.defaultCompanyId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, user?.defaultCompanyId]);

  /** If the member's initial company is not in the first 200 companies, store the name in the state
  otherwise the SelectBox will be empty */
  useEffect(() => {
    if (
      state.member.selectedCompany?.id &&
      !state.member.selectedCompany?.name &&
      accountCompanyData &&
      state.member.selectedCompany?.id === accountCompanyData?.id
    ) {
      dispatch({
        type: ACTIONS.SET_COMPANY,
        payload: {
          value: accountCompanyData,
        },
      });
    }
  }, [state.member.selectedCompany, accountCompanyData]);

  const { data: roleData, doFetch: doFetchRole } = useDataApi();

  // Get the member's initial role
  useEffect(() => {
    if (!accountId || !user?.defaultRoleId) return;

    doFetchRole(buildGetAccountRoleRequest(accountId, user?.defaultRoleId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, user?.defaultRoleId]);

  /** If the member's initial role is not in the first 200 roles, store the name in the state
  otherwise the SelectBox will be empty */
  useEffect(() => {
    if (
      state.member.selectedRole?.id &&
      !state.member.selectedRole?.name &&
      roleData &&
      state.member.selectedRole?.id === roleData?.id
    ) {
      dispatch({
        type: ACTIONS.SET_ROLE,
        payload: {
          value: roleData,
        },
      });
    }
  }, [state.member.selectedRole, roleData]);

  useEffect(() => {
    if (!user) return;

    const {
      defaultCompanyId,
      defaultCompanyName,
      defaultRoleId,
      defaultRoleName,
    } = user;

    dispatch({
      type: ACTIONS.SET_COMPANY,
      payload: {
        value:
          defaultCompanyId && defaultCompanyName
            ? { id: defaultCompanyId, name: defaultCompanyName }
            : null,
      },
    });

    dispatch({
      type: ACTIONS.SET_ROLE,
      payload: {
        value:
          defaultRoleId && defaultRoleName
            ? { id: defaultRoleId, name: defaultRoleName }
            : null,
      },
    });

    const selectedAccountLevels = () => {
      return Object.keys(user.accessLevels).reduce(
        (selectedAccesses, access) => {
          if (ACCESSES[access] && user.accessLevels[access]) {
            selectedAccesses.push({ value: ACCESSES[access].value });
          }

          return selectedAccesses;
        },
        []
      );
    };

    dispatch({
      type: ACTIONS.SET_ACCESS_LEVEL,
      payload: {
        value: selectedAccountLevels(),
      },
    });
  }, [ACCESSES, user]);

  useEffect(() => {
    if (!user) return;

    const availableAccessLevels = [ACCESSES.accountAdmin, ACCESSES.executive];

    if (user.accessLevels.projectAdmin)
      availableAccessLevels.push(ACCESSES.projectAdmin);
    if (user.accessLevels.projectMember)
      availableAccessLevels.push(ACCESSES.projectMember);

    setAccessLevels(availableAccessLevels);
  }, [ACCESSES, user]);

  const { activeAccountRoles, isFetchingAccountRoles, onChangeRoleInput } =
    useGetAccountRoles();

  const latestOnChangeRoleInput = useRef(onChangeRoleInput);

  useEffect(() => {
    latestOnChangeRoleInput.current = onChangeRoleInput;
  }, [onChangeRoleInput]);

  useEffect(() => {
    const handler = setTimeout(() => {
      latestOnChangeRoleInput.current(state.member.roleSearchInput.trim());
    }, 800);

    return () => clearTimeout(handler);
  }, [state.member.roleSearchInput]);

  const {
    data: updateMemberData,
    error: updateMemberError,
    doFetch: doUpdateMemberFetch,
    loading: updateMemberLoading,
  } = useDataApi();

  useEffect(() => {
    if (updateMemberData === null) return;

    dispatch({
      type: ACTIONS.SET_SAVE,
      payload: false,
    });

    const response = {
      status: OP_STATUSES.SUCCESS,
      response: updateMemberData,
    };

    if (latestOnSave.current) latestOnSave.current(response);
  }, [updateMemberData]);

  useEffect(() => {
    if (updateMemberError === null) return;

    dispatch({
      type: ACTIONS.SET_SAVE,
      payload: false,
    });

    const response = {
      status: OP_STATUSES.ERROR,
      response: updateMemberError,
    };

    if (latestOnSave.current) latestOnSave.current(response);
  }, [updateMemberError]);

  useEffect(() => {
    const getAccessLevels = () => {
      const accesses = {};

      state.member.selectedAccessLevel.forEach((access) => {
        if (
          access.value === ACCOUNT_LEVEL_ACCESSES.ACCOUNT_ADMIN ||
          access.value === ACCOUNT_LEVEL_ACCESSES.EXECUTIVE
        ) {
          accesses[access.value] = true;
        }
      });

      return accesses;
    };

    const saveMemberData = () => {
      const requestBody = {
        defaultCompanyId: state.member.selectedCompany
          ? state.member.selectedCompany.id
          : null,
        defaultRoleId: state.member.selectedRole
          ? state.member.selectedRole.id
          : null,
        accessLevels: getAccessLevels(),
      };

      doUpdateMemberFetch(
        ...buildUpdateAccountMemberRequest(accountId, user.id, requestBody)
      );
    };

    if (state.save) saveMemberData();
  }, [state, accountId, user, doUpdateMemberFetch]);

  useEffect(() => {
    if (!updateMemberLoading && isUpdatingCompany) {
      if (updateMemberError) {
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate('CHANGES_NOT_SAVED', {
            field: T.translate('COMPANY_LABEL'),
          }),
        });
      } else {
        showNotification({
          status: NOTIFICATION_STATUSES.SUCCESS,
          message: T.translate('CHANGES_SAVED', {
            field: T.translate('COMPANY_LABEL'),
          }),
        });

        dispatch({
          type: ACTIONS.SET_COMPANY_SEARCH_INPUT,
          payload: { value: '' },
        });
      }
      setIsUpdatingCompany(false);
      return;
    }
    if (!updateMemberLoading && isUpdatingRole) {
      if (updateMemberError) {
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate('CHANGES_NOT_SAVED', {
            field: T.translate('ROLE_LABEL'),
          }),
        });
      } else {
        showNotification({
          status: NOTIFICATION_STATUSES.SUCCESS,
          message: T.translate('CHANGES_SAVED', {
            field: T.translate('ROLE_LABEL'),
          }),
        });

        dispatch({
          type: ACTIONS.SET_ROLE_SEARCH_INPUT,
          payload: { value: '' },
        });
      }
      setIsUpdatingRole(false);
      return;
    }
    if (!updateMemberLoading && isUpdatingAccessLevel) {
      if (updateMemberError) {
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate('CHANGES_NOT_SAVED', {
            field: T.translate('ACCESS_LABEL'),
          }),
        });
      } else {
        showNotification({
          status: NOTIFICATION_STATUSES.SUCCESS,
          message: T.translate('CHANGES_SAVED', {
            field: T.translate('ACCESS_LABEL'),
          }),
        });
      }
      setIsUpdatingAccessLevel(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMemberLoading]);

  const handleCompanyInputChange = useCallback((value) => {
    dispatch({
      type: ACTIONS.SET_COMPANY_SEARCH_INPUT,
      payload: { value },
    });
  }, []);

  const handleCompanyChange = useCallback((value) => {
    dispatch({
      type: ACTIONS.SET_COMPANY,
      payload: { value, save: true },
    });

    setIsUpdatingCompany(true);
  }, []);

  const handleRoleInputChange = useCallback((value) => {
    dispatch({
      type: ACTIONS.SET_ROLE_SEARCH_INPUT,
      payload: { value },
    });
  }, []);

  const handleRoleChange = useCallback((value) => {
    dispatch({
      type: ACTIONS.SET_ROLE,
      payload: { value, save: true },
    });

    setIsUpdatingRole(true);
  }, []);

  const handleAccessLevelChange = useCallback((accesses) => {
    dispatch({
      type: ACTIONS.SET_ACCESS_LEVEL,
      payload: { value: accesses, save: true },
    });

    setIsUpdatingAccessLevel(true);
  }, []);

  // company creation
  const onAccountCompanyCreation = (companyId) => {
    handleCompanyChange({
      id: companyId,
      name: state.member.companySearchInput,
    });
  };

  const { createAccountCompanyRequest } = useCreateAccountCompany({
    onSuccess: onAccountCompanyCreation,
    onError: () =>
      dispatch({
        type: ACTIONS.SET_COMPANY_CREATE_ERROR,
        payload: { value: true },
      }),
  });

  const handleCreateCompany = (stateAndHelpers) => {
    createAccountCompanyRequest(state.member.companySearchInput);
    trackEvent(
      `${TRACK_EVENTS.SCOPE.ACCOUNT_ADMIN}.${TRACK_EVENTS.NAME.COMPANY}.${TRACK_EVENTS.NAME.CREATE}`,
      { origin: 'member_profile' }
    );
    stateAndHelpers.closeMenu();
  };

  if (!user) return null;

  return (
    <AccountMemberProfilePanel
      {...rest}
      accessLevels={accessLevels}
      companies={accountCompanies}
      companySearchInput={state.member.companySearchInput}
      companyCreateError={state.member.companyCreateError}
      isFetchingCompanies={isFetchingAccountCompanies}
      isFetchingRoles={isFetchingAccountRoles}
      isFetchingUserOfferings={isFetchingUserOfferings}
      licensingTrialMessages={licensingTrialMessages}
      loadingOfferings={loadingOfferings}
      offeringsDataError={offeringsDataError}
      overSheetLimitData={overSheetLimitData}
      savingDropdown={savingInputInfo}
      onAccessLevelChange={handleAccessLevelChange}
      onChangeCompanyInput={handleCompanyInputChange}
      onChangeRoleInput={handleRoleInputChange}
      onCompanyChange={handleCompanyChange}
      onCreateCompany={handleCreateCompany}
      onLicenseChange={handleLicenseChange}
      onRoleChange={handleRoleChange}
      onTryAgainClick={handleTryAgainClick}
      roles={activeAccountRoles}
      roleSearchInput={state.member.roleSearchInput}
      selectedAccessLevel={state.member.selectedAccessLevel}
      selectedCompany={state.member.selectedCompany}
      selectedRole={state.member.selectedRole}
      subscription={state.subscription}
      user={user}
      userOfferingsDataError={userOfferingsDataError}
    />
  );
};

AccountMemberProfilePanelContainer.displayName = 'AccountMemberProfile';

AccountMemberProfilePanelContainer.propTypes = {
  /** A value indicating if account offerings request is loading */
  loadingOfferings: PropTypes.bool.isRequired,
  /** Account offerings to generate license information */
  offerings: PropTypes.arrayOf(PropTypes.object),
  /** A value indicating if there is an error fetching offerings data */
  offeringsDataError: PropTypes.bool.isRequired,
  /** Callback executed when the user clicks on the Remove member option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onRemove: PropTypes.func,
  /** Callback executed when the user clicks on the Resend invitation option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onResend: PropTypes.func,
  /** Whenever data is saved, this callback is invoked with operation status */
  onSaveData: PropTypes.func,
  /** Whenever license data is saved, this callback is invoked with the operation information */
  onUpdateOffering: PropTypes.func,
  /** Callback executed when the user clicks on the Try Again link
   * in the Member Subscription error section */
  onTryAgainClick: PropTypes.func.isRequired,
  /** Project member data to display in the modal */
  user: accountUserPropType,
};

AccountMemberProfilePanelContainer.defaultProps = {
  offerings: null,
  onRemove: () => {},
  onResend: () => {},
  onSaveData: () => {},
  onUpdateOffering: () => {},
  user: undefined,
};

AccountMemberProfilePanelContainer.opStatuses = OP_STATUSES;

export default AccountMemberProfilePanelContainer;
